import { Injectable, inject } from '@angular/core';
import { APP_CONFIG } from '../config/app-config.token';
import { AppConfig } from '../config/app-config.model';

/** Global API installed by the Rybbit analytics script once loaded. */
interface RybbitApi {
  event?: (eventName: string, properties?: Record<string, unknown>) => void;
  pageview?: (path?: string) => void;
}

/**
 * Analytics Service
 *
 * Handles loading and initialization of analytics scripts (Rybbit).
 * The script URL and site ID are configured per environment to support
 * different analytics endpoints for internal vs external users.
 *
 * Internal users: stats.fed.diva.net (siteId: 2)
 * External users: stats.abvv.be (siteId: 1)
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private config = inject<AppConfig>(APP_CONFIG);


  private scriptLoaded = false;
  private eventQueue: Array<{ eventName: string; properties?: Record<string, unknown> }> = [];

  /**
   * Initialize analytics by loading the configured script.
   * Should be called once during app initialization.
   *
   * Skips loading on localhost to avoid polluting analytics during development.
   */
  init(): void {
    if (this.scriptLoaded) {
      return;
    }

    // Skip analytics on localhost
    if (this.isLocalhost()) {
      return;
    }

    // Check if rybbit config exists in environment
    const rybbitConfig = this.config.rybbit;
    if (!rybbitConfig?.url || !rybbitConfig?.siteId) {
      return;
    }

    this.loadRybbitScript(rybbitConfig.url, rybbitConfig.siteId);
  }

  /**
   * Load the Rybbit analytics script dynamically.
   */
  private loadRybbitScript(url: string, siteId: string): void {
    try {
      const script = document.createElement('script');
      script.src = url;
      script.dataset['siteId'] = siteId;
      script.defer = true;
      script.async = true;

      script.onload = () => {
        this.scriptLoaded = true;
        this.flushEventQueue();
      };

      script.onerror = (error) => {
        console.error('[AnalyticsService] Failed to load Rybbit script:', error);
      };

      document.head.appendChild(script);
    } catch (error) {
      console.error('[AnalyticsService] Error loading Rybbit script:', error);
    }
  }

  /**
   * Check if the app is running on localhost.
   */
  private isLocalhost(): boolean {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  }

  /**
   * Flush any queued events after script loads.
   */
  private flushEventQueue(): void {
    if (this.eventQueue.length > 0) {
      const queue = [...this.eventQueue];
      this.eventQueue = [];
      queue.forEach(({ eventName, properties }) => {
        this.trackEvent(eventName, properties);
      });
    }
  }

  /**
   * Track a custom event.
   *
   * @param eventName - Name of the event (e.g., 'download_attestation', 'login_success')
   * @param properties - Optional properties to attach to the event
   *
   * @example
   * // Simple event
   * this.analyticsService.trackEvent('logout');
   *
   * // Event with properties
   * this.analyticsService.trackEvent('download_document', {
   *   documentType: 'attestation',
   *   year: 2024
   * });
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (this.isLocalhost()) {
      return;
    }

    if (!this.scriptLoaded) {
      this.eventQueue.push({ eventName, properties });
      return;
    }

    try {
      const rybbit = (window as Window & { rybbit?: RybbitApi }).rybbit;

      if (rybbit?.event) {
        rybbit.event(eventName, properties);
      }
    } catch (error) {
      console.error('[AnalyticsService] Error tracking event:', error);
    }
  }

  /**
   * Track a page view manually.
   * Rybbit tracks page views automatically, but this can be used for SPA navigation
   * if automatic tracking doesn't catch all route changes.
   *
   * @param path - Optional custom path (defaults to current URL)
   */
  trackPageView(path?: string): void {
    if (this.isLocalhost()) {
      return;
    }

    if (!this.scriptLoaded) {
      return;
    }

    try {
      const rybbit = (window as Window & { rybbit?: RybbitApi }).rybbit;
      if (rybbit?.pageview) {
        rybbit.pageview(path);
      }
    } catch (error) {
      console.error('[AnalyticsService] Error tracking page view:', error);
    }
  }
}
